/**
 * Where the page goes when the reader changes the active organization (app#471).
 *
 * Pure, so `orgSwitch.test.ts` can check it with `node --experimental-strip-types`.
 */

/** The first path segments whose SECOND segment is the id of one object of an organization. */
const NODE_PAGES = new Set(['nodes'])
/** `network/<id>` is the popup that edits one network: its list is the network list. */
const NETWORK_PAGES = new Set(['networks', 'network'])
/** Popups that are about one node, network or service: their list is the node list. */
const OBJECT_POPUPS = new Set(['node', 'compute', 'service', 'permissions'])

/**
 * The org's home view: the map or the graph, with no pane open. ONE constant: the selector and a
 * page that closes by itself (`useCloseWhenMissing`) go to the same place.
 */
export const HOME = '/'

/**
 * The path to go to after a change of organization, or null to stay.
 *
 * ⚠⚠ A PAGE ABOUT ONE NODE, NETWORK OR SERVICE DOES NOT FOLLOW THE READER TO ANOTHER ORGANIZATION.
 * The stores load the new organization's rows, the object of the old one is not among them, and
 * the page then drew an empty pane (`Node` returns null for a node it does not have). Even when
 * the new organization can see the same node, the reader chose another organization, not that
 * node again (Benoit, 2026-10-07).
 * ⚠⚠ IT GOES TO THE HOME VIEW, NOT TO A LIST (Benoit, 2026-10-07: "if the URL parameters don't
 * match the org, redirect home"). It is the place a page goes to when it finds by itself that its
 * object is not in the active organization (`useCloseWhenMissing`, app#473), so the two agree.
 * ⚠ Only these pages. A path of another kind stays, also when it has two segments: the pages a
 * mailed link opens (`/invite/<token>`, `/access/<service>`, `/register/<code>`), the first-run
 * pages (`/onboard/node`), and the pages about the person (`/account/<id>`).
 * ⚠ A page with no object in its path stays where it is: the lists, Usage, Billing, Members and
 * the popups that make a new object (`/node`, `/network`) are about the ACTIVE organization, and
 * they load the new one by themselves.
 */
export function pathAfterOrgSwitch(pathname: string): string | null {
  return listOfObjectPage(pathname) ? HOME : null
}

/**
 * The LIST of the kind of a page about one object (`/nodes`, `/networks`), or null for a page
 * that is not about one object.
 *
 * ⚠ For the drawer only. The drawer remembers the page it opens again, and that page must be one
 * of its tabs: the home view is the drawer CLOSED, so it cannot be the page the drawer opens.
 */
export function listOfObjectPage(pathname: string): string | null {
  const [page, object] = pathname.split('/').filter(Boolean)
  if (!page || !object) return null
  if (NODE_PAGES.has(page)) return '/nodes'
  if (NETWORK_PAGES.has(page)) return '/networks'
  if (OBJECT_POPUPS.has(page)) return '/nodes'
  return null
}
